import React from 'react';

import styled from 'styled-components';

import Button from '@adsk/alloy-react-button';
import { DocumentTextIcon, XIcon, TrashCanIcon } from '@adsk/alloy-react-icon';
import ProgressBar from '@adsk/alloy-react-progress-bar';
import ToolTip from '@adsk/alloy-react-tooltip';

import { UPLOAD_STATUS } from './helpers/consts';
import { useTranslation } from './i18n';

const {
  INITIALIZING,
  UPLOADING,
  UPLOAD_SUCCESS,
  UPLOAD_FAILED,
  PROCESSING,
  PROCESS_FAILED,
} = UPLOAD_STATUS;

const ProgressWrapper = styled.div`
  padding: 4px 0;
`;

const Subtitle = styled.p`
  margin: 4px;
  color: #8595a1;
`;

const ErrorText = styled.p`
  color: #af302b;
`;

const FileIcon = styled(DocumentTextIcon).attrs({ size: 32 })`
  padding-top: 8px;
`;

const Item = styled.div`
  display: flex;
  width: 100%;
  height: 56px;
  text-align: left;
  justify-content: space-between;
`;

const NameStatus = styled.div`
  flex: 2;
  padding: 4px;
  overflow: hidden;
`;

const FileName = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 4px;
  color: #1f323d;
`;

const CancelButton = styled(Button).attrs({
  variant: 'tertiary',
  renderIcon: XIcon.renderIcon,
})`
  color: #8595a1;
`;

const RemoveButton = styled(Button).attrs({
  variant: 'tertiary',
  renderIcon: TrashCanIcon.renderIcon,
})`
  color: #af302b;
`;

const UploadItem = React.forwardRef(({ file, onRemove }, ref) => {
  const { t } = useTranslation();
  const {
    ref: { name: filename },
    status,
    progress,
    isValid,
  } = file;

  /**
   *  INITIALIZING:     the default upload count in parallel is 6, exceed files are at INITIALIZING status.
   *  UPLOADING:        upload to OSS, show progress
   *  UPLOAD_SUCCESS:   upload to OSS done
   *  PROCESSING:       send upload command successfully, the following actions are asynchronous, docs plugin does not
   *                    handler those actions.
   *  UPLOAD_FAILED:    upload to OSS failed
   *  PROCESS_FAILED:   send upload command failed.
   *
   *  About cancel, Here suppose all cancel actions executed successfully to simplify UI status, cancel failed is imperceptibility on UI.
   */
  const getUploadSubtitle = (status, isValid) => {
    if (!isValid) {
      return <Subtitle>{t('UPLOAD_INVALID')}</Subtitle>;
    }

    switch (status) {
      case INITIALIZING:
        return <Subtitle>{t('UPLOAD_PENDING')}</Subtitle>;
      case UPLOADING:
        return (
          <ProgressWrapper>
            <ProgressBar now={progress} />
          </ProgressWrapper>
        );
      case UPLOAD_SUCCESS:
        return (
          <ProgressWrapper>
            <ProgressBar now={100} />
          </ProgressWrapper>
        );
      case PROCESSING:
        return <Subtitle>{t('UPLOAD_COMPLETE')}</Subtitle>;
      case UPLOAD_FAILED:
      case PROCESS_FAILED:
        return <ErrorText>{t('UPLOAD_FAILED')}</ErrorText>;
      default:
        return null;
    }
  };

  const canCancel = [INITIALIZING, UPLOADING].includes(status);
  const canRemove = [
    UPLOAD_SUCCESS,
    UPLOAD_FAILED,
    PROCESSING,
    PROCESS_FAILED,
  ].includes(status);

  return (
    <Item ref={ref}>
      <FileIcon />
      <NameStatus>
        <ToolTip content={filename}>
          <FileName>{filename}</FileName>
        </ToolTip>
        {getUploadSubtitle(status, isValid)}
      </NameStatus>
      {canCancel && <CancelButton onClick={() => onRemove(file)} />}
      {canRemove && <RemoveButton onClick={() => onRemove(file)} />}
    </Item>
  );
});

UploadItem.displayName = 'UploadItem';

export default UploadItem;
